/**
 * What to show on the 1099 tax documents page.
 *
 * Kept out of the component so it can be tested: the embedded components mount
 * a cross-origin Stripe iframe, which needs a real browser, so the decision
 * lives here and the page only maps state to JSX. Same split as
 * `returnState.js` and `refreshState.js`.
 *
 * Order matters, and each check earns its place:
 *
 * - Signed-out first. Requesting a session without an identity 401s, and the
 *   response interceptor clears localStorage and redirects home — the user
 *   loses their session on top of the page they came for.
 * - Unknown status before missing account. `status` starts null and lands a
 *   round-trip later; reporting "no payout account" on absent status tells a
 *   fully onboarded gigster to go set up payouts they already have. That exact
 *   bug is documented in `returnState.js:51-54`.
 */
export const TAX_STATES = {
  /** No session — a fetch would 401 and log them out. */
  SIGNED_OUT: "signedOut",
  /** Identity present; waiting on the account status or the session. */
  LOADING: "loading",
  /** A payee who has not set up payouts yet — send them to onboarding. */
  NO_PAYOUT_ACCOUNT: "noPayoutAccount",
  /**
   * Someone who never gets paid, so there is no 1099 and never will be.
   * Distinct from NO_PAYOUT_ACCOUNT because "set up payouts first" is advice a
   * homeowner cannot act on and should not be given.
   */
  NOT_A_PAYEE: "notAPayee",
  /** Client secret in hand; the embedded components can mount. */
  READY: "ready",
  /** The session request came back without a secret — offer a retry. */
  FAILED: "failed",
  /**
   * Stripe's email linked to one connected account and a different one is
   * signed in. Nothing is leaked — the session is always minted for the signed-
   * in user — but showing their own tax details under someone else's link is
   * confusing, so say which account the link was for instead.
   */
  WRONG_ACCOUNT: "wrongAccount",
  /**
   * The one-time handoff code was spent, expired or refused, or the page was
   * reloaded after it was stripped from the URL. Not a retry: the code cannot
   * be used twice, so the only way forward is back to the app for a new one.
   */
  SESSION_EXPIRED: "sessionExpired",
  /** Too many attempts; the same link still works after a short wait. */
  THROTTLED: "throttled",
};

const COPY = {
  [TAX_STATES.SIGNED_OUT]: {
    title: "TAX_DOCUMENTS",
    description: "TAX_SIGNED_OUT_DESC",
  },
  [TAX_STATES.LOADING]: {
    title: "TAX_DOCUMENTS",
    description: "TAX_LOADING_DESC",
  },
  [TAX_STATES.NO_PAYOUT_ACCOUNT]: {
    title: "TAX_NO_PAYOUT_ACCOUNT",
    description: "TAX_NO_PAYOUT_ACCOUNT_DESC",
  },
  [TAX_STATES.NOT_A_PAYEE]: {
    title: "TAX_NOT_A_PAYEE",
    description: "TAX_NOT_A_PAYEE_DESC",
  },
  [TAX_STATES.READY]: {
    title: "TAX_DOCUMENTS",
    description: "TAX_DOCUMENTS_DESC",
  },
  [TAX_STATES.FAILED]: {
    title: "TAX_UNAVAILABLE",
    description: "TAX_UNAVAILABLE_DESC",
  },
  [TAX_STATES.WRONG_ACCOUNT]: {
    title: "TAX_WRONG_ACCOUNT",
    description: "TAX_WRONG_ACCOUNT_DESC",
  },
  [TAX_STATES.SESSION_EXPIRED]: {
    title: "TAX_SESSION_EXPIRED",
    description: "TAX_SESSION_EXPIRED_DESC",
  },
  [TAX_STATES.THROTTLED]: {
    title: "TAX_THROTTLED",
    description: "TAX_THROTTLED_DESC",
  },
};

/**
 * The state to render.
 *
 * `hasPayoutAccount` is the backend's answer, not a guess from local data:
 * `receivingAccountVerify` lags the live Connect status under Stripe, so the
 * page asks the tax endpoint, which reports `noPayoutAccount` without creating
 * an account as a side effect.
 */
export const resolveTaxState = ({
  signedIn,
  statusLoaded = false,
  hasPayoutAccount,
  isPayee = true,
  clientSecret,
  accountId,
  linkedAccountId,
  failed = false,
  codeRejected = false,
  sessionExpired = false,
  handoffLost = false,
  throttled = false,
}) => {
  if (codeRejected || sessionExpired || handoffLost) {
    return TAX_STATES.SESSION_EXPIRED;
  }
  if (!signedIn) {
    return TAX_STATES.SIGNED_OUT;
  }
  if (!statusLoaded) {
    return TAX_STATES.LOADING;
  }
  if (hasPayoutAccount === false) {
    return isPayee ? TAX_STATES.NO_PAYOUT_ACCOUNT : TAX_STATES.NOT_A_PAYEE;
  }
  if (clientSecret) {
    // Stripe appends `stripe_account_id` to the links in its emails. Compared
    // only when both sides are known: most arrivals carry no such parameter,
    // and a missing one is not a mismatch.
    if (linkedAccountId && accountId && linkedAccountId !== accountId) {
      return TAX_STATES.WRONG_ACCOUNT;
    }
    return TAX_STATES.READY;
  }
  if (throttled) {
    return TAX_STATES.THROTTLED;
  }
  return failed ? TAX_STATES.FAILED : TAX_STATES.LOADING;
};

/** i18n keys for a state. */
export const getTaxCopy = (state) => COPY[state];

/** Only fetch a session once there is an identity to fetch it with. */
export const shouldRequestSession = (state) =>
  state === TAX_STATES.LOADING || state === TAX_STATES.READY;

/** Offer payout onboarding instead of an empty tax page. */
export const needsPayoutSetup = (state, handoff = false) =>
  state === TAX_STATES.NO_PAYOUT_ACCOUNT && !handoff;

/**
 * A failed session is worth retrying; nothing else is. A handoff visit can only
 * retry while its code is still unspent (`retryable`: it never reached the
 * backend or was throttled); once spent it goes back to the app instead.
 */
export const canRetry = (state, handoff = false, retryable = false) =>
  state === TAX_STATES.THROTTLED ||
  (state === TAX_STATES.FAILED && (!handoff || retryable));

const HANDOFF_APP_STATES = [
  TAX_STATES.SESSION_EXPIRED,
  TAX_STATES.NO_PAYOUT_ACCOUNT,
  TAX_STATES.NOT_A_PAYEE,
  TAX_STATES.FAILED,
];

/**
 * Someone who arrived with a handoff code has no web session, so the web's own
 * next steps (sign in, payout setup, retry) all dead-end. The app is where
 * each of those is actually possible.
 */
export const needsOpenApp = (state, handoff, retryable = false) =>
  Boolean(handoff) &&
  HANDOFF_APP_STATES.includes(state) &&
  !(state === TAX_STATES.FAILED && retryable);

/** Signed-out visitors get a sign-in prompt, not a payout action. */
export const needsSignIn = (state) => state === TAX_STATES.SIGNED_OUT;
